import { useEffect, useState } from "react";
import { DownOutlined } from "@ant-design/icons";
import { Space } from "antd";

import { type LanguageOption, useHardcodedLanguages } from "@av/config/i18n/locale-config";
import { useLocaleStore } from "@av/utils/hooks/useLocaleStore";
import { Dropdown, type ItemType } from "../atoms/Dropdown";
import { type Locale } from "../organisms/type";

function getLanguageLabel(language: string, supportedLanguages: LanguageOption[]): string {
  const currentLang = supportedLanguages.find((lang) => lang.value === language);

  return currentLang ? currentLang.label : supportedLanguages[0].label;
}

type LoggedoutLanguageSelectorProps = {
  onLanguageChange?: (locale: Locale) => void;
};

function LoggedoutLanguageSelector({ onLanguageChange }: LoggedoutLanguageSelectorProps) {
  const { locale } = useLocaleStore();
  const { setLocale } = useLocaleStore();

  //Note: Hardcoding languages for now because our feature flag require user authorization
  // and this is used outside of that route
  const supportedLanguages = useHardcodedLanguages();

  const [currentLanguageDisplay, setCurrentLanguageDisplay] = useState<string>(
    getLanguageLabel(locale, supportedLanguages)
  );

  const handleLanguageChange = (langKey: Locale, langLabel: string) => {
    if (onLanguageChange) {
      onLanguageChange(langKey);

      return;
    }

    setLocale(langKey);
    setCurrentLanguageDisplay(langLabel);
  };

  // Keep label aligned with the shared locale store across all apps/pages
  useEffect(() => {
    setCurrentLanguageDisplay(getLanguageLabel(locale, supportedLanguages));
  }, [locale, supportedLanguages]);

  const languageMenuItems: ItemType[] = supportedLanguages.map((lang) => ({
    key: lang.value,
    label: lang.label,
    onClick: () => handleLanguageChange(lang.value, lang.label),
    "data-testid": `language-option-${lang.value}`,
  }));

  return (
    <Dropdown menu={{ items: languageMenuItems }} trigger={["click"]} testIdPrefix="languages">
      <a onClick={(e) => e.preventDefault()} style={{ color: "#555" }}>
        <Space>
          {currentLanguageDisplay}
          <DownOutlined />
        </Space>
      </a>
    </Dropdown>
  );
}

export { LoggedoutLanguageSelector };
